import * as React from 'react';
import { createPortal } from 'react-dom';
import { Popper } from 'react-popper';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { cs } from '@shared/utils/utils-classname';
import { Tooltip } from '@shared/ui-react/Tooltip';

const tooltipModifiers = [
    {
        name: 'offset',
        options: {
            offset: [0, 11],
        },
    },
];

@observer
export class Button extends React.PureComponent<{
    icon?: string;
    iconPosition?: 'left' | 'right';
    iconSize?: number;

    onClick?: any;
    onContextMenu?: any;
    className?: string;
    tooltip?: React.ReactNode;
    popover?: React.ReactNode;
    popoverPlacement?: 'top' | 'bottom' | 'left' | 'right';
    title?: string;
    disabled?: boolean;
    loading?: boolean;
    children?: any;
    id?: string;
    style?: React.CSSProperties;
    element?: 'button' | 'div';
    type?: 'button' | 'submit';

    // style
    primary?: boolean;
    secondary?: boolean;
    subtle?: boolean;
    large?: boolean;
    small?: boolean;
}> {
    public el: HTMLElement;
    public popoverEl: HTMLElement;
    popoverCloseTimeout: any = null;
    @observable isPopoverOpen = false;

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    componentWillUnmount() {
        this.clearPopoverCloseTimeout();
    }

    render() {
        let { children, onClick, onContextMenu, tooltip, popover, loading, disabled, icon, title, id, style, type, iconPosition, popoverPlacement } = this.props;

        iconPosition = iconPosition ?? 'left';
        popoverPlacement = popoverPlacement ?? 'top';

        const hasPopover = !!popover;
        const isDefault = !this.props.primary && !this.props.secondary && !this.props.className?.includes('button-');
        const isSubtle = isDefault ? false : this.props.subtle;
        const isSecondary = isDefault ? true : this.props.secondary;

        const classes = cs(this.props.className, {
            loading,
            disabled,
            'ik-button-primary': this.props.primary && !isSubtle,
            'ik-button-primary--subtle': this.props.primary && isSubtle,
            'ik-button-secondary': isSecondary && !isSubtle,
            'ik-button-secondary--subtle': isSecondary && isSubtle,
            'ik-button-small': this.props.small,
            'ik-button-large': this.props.large,
        });

        if (icon) {
            if (icon.startsWith('iki-')) {
                icon = 'iki ' + icon;
            } else if (icon.startsWith('pi-')) {
                icon = 'pi ' + icon;
            }
        }

        const BtnInside = (
            <>
                {icon && iconPosition === 'left' && (
                    <i
                        className={icon.includes('pi-') || icon.includes('iki-') ? icon : 'iconf-' + icon}
                        style={{ fontSize: this.props.iconSize ? this.props.iconSize + 'px' : null }}
                    ></i>
                )}
                {children ? <span>{children}</span> : null}
                {icon && iconPosition === 'right' && (
                    <i
                        className={icon.includes('pi-') || icon.includes('iki-') ? icon : 'iconf-' + icon}
                        style={{ fontSize: this.props.iconSize ? this.props.iconSize + 'px' : null }}
                    ></i>
                )}
            </>
        );

        const ButtonElem = this.props.element ?? 'button';

        const Btn = (
            <ButtonElem
                ref={el => {
                    this.el = el;
                    this.popoverEl = hasPopover ? el : null;
                }}
                onClick={onClick}
                onContextMenu={onContextMenu}
                onMouseEnter={hasPopover ? this.openPopover : null}
                onMouseLeave={hasPopover ? this.schedulePopoverClose : null}
                className={classes}
                style={style}
                disabled={disabled || loading}
                type={type ?? 'button'}
                id={id}
                title={title}
            >
                {BtnInside}
            </ButtonElem>
        );

        if (hasPopover) {
            return (
                <>
                    {Btn}
                    {this.isPopoverOpen &&
                        this.popoverEl &&
                        createPortal(
                            <Popper referenceElement={this.popoverEl} placement={popoverPlacement}>
                                {({ ref, style: popoverStyle, placement }) => (
                                    <div
                                        ref={ref}
                                        style={{ ...popoverStyle, pointerEvents: 'auto' }}
                                        className='popover-container'
                                        data-placement={placement}
                                        onMouseEnter={this.openPopover}
                                        onMouseLeave={this.schedulePopoverClose}
                                    >
                                        {popover}
                                    </div>
                                )}
                            </Popper>,
                            document.body
                        )}
                </>
            );
        } else if (tooltip) {
            return (
                <Tooltip tooltip={tooltip} placement='top' hideArrow={false} modifiers={tooltipModifiers} portalContainer={document.body}>
                    {Btn}
                </Tooltip>
            );
        } else {
            return Btn;
        }
    }

    openPopover = () => {
        if (!this.props.popover || this.props.disabled || this.props.loading) return;
        this.clearPopoverCloseTimeout();
        this.isPopoverOpen = true;
    };

    schedulePopoverClose = () => {
        if (!this.props.popover) return;
        this.clearPopoverCloseTimeout();
        this.popoverCloseTimeout = setTimeout(this.closePopoverNow, 320);
    };

    closePopoverNow = () => {
        this.clearPopoverCloseTimeout();
        this.isPopoverOpen = false;
    };

    clearPopoverCloseTimeout = () => {
        if (this.popoverCloseTimeout) {
            clearTimeout(this.popoverCloseTimeout);
            this.popoverCloseTimeout = null;
        }
    };
}
